/**
 * @file コンポーネント：検索機能付きフォームプルダウン ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// コンポーネントを返す
export const SearchSelect = (props) => {
    /**
     * プルダウンの各要素
     */
    const refSearch = useRef(null); // 検索フィールド
    const refSearchlist = useRef(null); // 検索候補リスト
    const refSelect = useRef(null); // 本当のプルダウン

    /**
     * 選択中テキスト状態管理
     */
    const [textSelected, setTextSelected] = useState(null); // 選択中テキスト
    useEffect(() => {
        if (props.list.length > 0) {
            if (props.value) {
                // 初期指定されている場合
                for (const item of props.list) {
                    if (item.value === props.value) {
                        setTextSelected(item.text);
                        break;
                    }
                }
            } else {
                // 初期指定されていない場合
                setTextSelected(props.list[0].text);
            }
        }
    }, [props.list, props.value]);

    /**
     * 検索モーダルの開閉状態管理
     */
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        if (refSearch !== null && refSearch.current !== null) {
            const searchlist = refSearchlist.current;
            if (isOpen === true) {
                // 検索候補リストのスクロール位置を初期状態に戻す
                searchlist.scrollTo(0, 0);
                // 開いた時に選択中の項目をアクティブにする
                const index =
                    refSelect.current.selectedIndex === -1
                        ? 0
                        : refSelect.current.selectedIndex;
                const value = refSelect.current.options[index].value;
                // 検索候補リストをアクティブの項目位置までスクロールする
                const target = searchlist.querySelector(
                    '[data-value="' + value + '"]'
                );
                target.classList.add("is-active");
                const positionY =
                    target.getBoundingClientRect().top -
                    searchlist.getBoundingClientRect().top;
                searchlist.scrollTo(0, positionY);
            } else {
                // 検索フィールドを空にする
                refSearch.current.value = "";
                searchlist.querySelectorAll("li").forEach((item) => {
                    // 全ての項目を非アクティブにする
                    item.classList.remove("is-active");
                    // 全ての項目を表示する
                    item.style.display = "list-item";
                });
                // モーダル内でマウスを動かしたか検知する処理のリセット
                setClientX(null);
                setClientY(null);
                setIsMouseMoved(false);
            }
        }
    }, [isOpen]);

    /**
     * 親コンポーネントに選択中の値を渡す
     */
    useEffect(() => {
        if (
            props.fnParent &&
            refSelect !== null &&
            refSelect.current !== null
        ) {
            props.fnParent(refSelect.current.value);
        }
    });

    /**
     * 検索フィールド入力時に検索候補リストの項目から該当項目を検索する
     * @param e イベント
     */
    const fnOnInputSearch = (e) => {
        const lowercaseValue = e.target.value.toLowerCase();
        refSearchlist.current.querySelectorAll("li").forEach((item) => {
            const lowercaseThisText = item.textContent.toLowerCase();
            // 大文字・小文字どちらでもヒットするように、全て小文字にして比較する
            if (lowercaseThisText.indexOf(lowercaseValue) === -1) {
                // 含まれていなければ非表示
                item.style.display = "none";
            } else {
                // 含まれていれば表示
                item.style.display = "list-item";
            }
        });
    };

    /**
     * 検索候補リストから選択する
     * @param e イベント
     */
    const fnOnClickSearchlistItem = (e) => {
        // 選択中テキストの変更
        setTextSelected(e.target.textContent);
        // 本当のプルダウンの選択状態を変更する
        const value = e.target.dataset.value;
        refSelect.current.value = value;
        // fnOnChange関数が渡されている場合、valueを返す
        if (props.fnOnChange !== undefined) {
            props.fnOnChange(value);
        }
        e.target.classList.add("is-active");
        // 検索モーダルを閉じる
        setIsOpen(false);
    };

    /**
     * 検索候補リストの項目にマウスオンした時にアクティブ状態にする
     * @param e イベント
     * @param isMouseMoved モーダル内でマウスを動かしたフラグ
     */
    const fnOnMouseEnterSearchlistItem = (e, isMouseMoved) => {
        if (isMouseMoved === true) {
            // 全ての項目のアクティブ状態を解除
            refSearchlist.current.querySelectorAll("li").forEach((item) => {
                item.classList.remove("is-active");
            });
            // 該当の項目をアクティブ状態にする
            e.target.classList.add("is-active");
        }
    };

    /**
     * モーダル内でマウスを動かしたか検知する処理
     * @param e イベント
     */
    const [isMouseMoved, setIsMouseMoved] = useState(false); // モーダル内でマウスを動かしたフラグ
    const [clientX, setClientX] = useState(null); // マウスX座標
    const [clientY, setClientY] = useState(null); // マウスY座標
    const fnOnMouseMove = (e) => {
        if (clientX === null) {
            setClientX(e.clientX);
        } else if (clientX !== e.clientX) {
            setIsMouseMoved(true);
        }
        if (clientY === null) {
            setClientY(e.clientY);
        } else if (clientY !== e.clientY) {
            setIsMouseMoved(true);
        }
    };

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            fnOnClickSearchlistItem={fnOnClickSearchlistItem}
            fnOnInputSearch={fnOnInputSearch}
            fnOnMouseEnterSearchlistItem={fnOnMouseEnterSearchlistItem}
            fnOnMouseMove={fnOnMouseMove}
            isMouseMoved={isMouseMoved}
            isOpen={isOpen}
            refSearch={refSearch}
            refSearchlist={refSearchlist}
            refSelect={refSelect}
            setIsMouseMoved={setIsMouseMoved}
            setIsOpen={setIsOpen}
            textSelected={textSelected}
        />
    );
};
